// src/utils/extractPromptCoach.ts

import { ExtractionResult } from "../interfaces/promptCoachTypes";
import { createLogger } from "./logger";

const log = createLogger("extractPromptCoach");

/**
 * Extract Prompt Coach section from markdown content
 *
 * Separates the markdown into three parts:
 * 1. Content before Prompt Coach
 * 2. Prompt Coach section (if found)
 * 3. Content after Prompt Coach
 *
 * This allows MessageBubble to render the main content as markdown
 * and the Prompt Coach as an interactive component.
 *
 * @param markdownContent - Full markdown string
 * @param isStreaming - Whether content is being streamed (suppresses expected warnings)
 * @returns ExtractionResult with separated content
 */
export function extractPromptCoach(
  markdownContent: string,
  isStreaming: boolean = false
): ExtractionResult {
  // Handle empty or invalid input
  if (!markdownContent || markdownContent.trim() === "") {
    return {
      contentBefore: "",
      promptCoachSection: null,
      contentAfter: "",
      headingDetected: false,
    };
  }

  // Find Prompt Coach heading (H1-H4 or plain text, case-insensitive)
  // Matches: "## Prompt Coach" (preferred), "## **Prompt Coach**" (bold), "## *Prompt Coach*" (italic)
  // Made flexible to handle cases where LLM doesn't output heading markers or uses markdown formatting
  const headingRegex = /^(#{1,4}\s+)?[\*_]*Prompt Coach[\*_]*\s*$/im;
  const headingMatch = markdownContent.match(headingRegex);

  // If no Prompt Coach heading found, check for XML tags as fallback
  if (!headingMatch || headingMatch.index === undefined) {
    // Fallback: Check if valid Prompt Coach XML tags are present without heading
    // Case-sensitive to prevent false positives with lowercase tags
    const xmlTagRegex = /<(STRATEGIC_QUESTION|PROMPT_ENHANCEMENT|NEXT_STEPS)>/;
    const xmlMatch = markdownContent.match(xmlTagRegex);

    if (xmlMatch && xmlMatch.index !== undefined) {
      // Found XML tags without heading - extract as Prompt Coach section
      const contentBefore = markdownContent.substring(0, xmlMatch.index).trim();
      const promptCoachSection = markdownContent.substring(xmlMatch.index).trim();

      // Only log if NOT streaming (expected during streaming)
      if (!isStreaming) {
        log.info("Prompt Coach detected via XML tags (no heading found)");
      }

      return {
        contentBefore,
        promptCoachSection,
        contentAfter: "",
        headingDetected: true, // Trigger panel rendering
      };
    }

    // No heading and no valid XML tags - not a Prompt Coach section
    return {
      contentBefore: markdownContent,
      promptCoachSection: null,
      contentAfter: "",
      headingDetected: false,
    };
  }

  // Extract content before Prompt Coach
  const contentBefore = markdownContent.substring(0, headingMatch.index).trim();

  // Find the end of Prompt Coach section (next heading or EOF)
  const startIndex = headingMatch.index;
  const restOfContent = markdownContent.substring(startIndex);

  // Skip the Prompt Coach heading itself and look for the NEXT heading
  // We need to search after the current heading line
  const afterHeading = restOfContent.substring(headingMatch[0].length);
  const nextHeadingMatch = afterHeading.match(/^#{1,4}\s+/m);

  let promptCoachSection: string;
  let contentAfter: string;

  if (nextHeadingMatch && nextHeadingMatch.index !== undefined) {
    // Found next heading - extract until that point
    // Calculate position in original restOfContent (add back the heading length we skipped)
    const nextHeadingPosInRest = headingMatch[0].length + nextHeadingMatch.index;
    promptCoachSection = restOfContent.substring(0, nextHeadingPosInRest).trim();
    contentAfter = restOfContent.substring(nextHeadingPosInRest).trim();
  } else {
    // No next heading - Prompt Coach extends to EOF
    promptCoachSection = restOfContent.trim();
    contentAfter = "";
  }

  // Edge case: Check for multiple Prompt Coach sections (malformed output)
  // Use global flag to count actual number of matches (not including capture groups)
  const headingRegexGlobal = /^(#{1,4}\s+)?Prompt Coach\s*$/gim;
  const multipleHeadings = (markdownContent.match(headingRegexGlobal) || []).length;
  if (multipleHeadings > 1) {
    log.warn("Multiple Prompt Coach sections detected. Using first occurrence only.");
  }

  // Edge case: Empty Prompt Coach section
  if (!promptCoachSection || promptCoachSection === headingMatch[0]) {
    // Only warn if NOT streaming (expected during streaming)
    if (!isStreaming) {
      log.warn("Prompt Coach section is empty or contains only heading");
    }
    return {
      contentBefore: markdownContent,
      promptCoachSection: null,
      contentAfter: "",
      headingDetected: true, // Heading was found, even though section is empty
    };
  }

  return {
    contentBefore,
    promptCoachSection,
    contentAfter,
    headingDetected: true, // Heading was found and section extracted
  };
}
